import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RegistrationData } from '../types/Auth';
import { decodeRegistrationToken, register } from '../api/AuthAPI';
import { getUserMessage, logApiError, parseApiError } from '../api/apiError';

const RegistrationForm: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('email') ?? '';

    const [email, setEmail] = useState('');
    const [tokenError, setTokenError] = useState<string | null>(null);
    const [formData, setFormData] = useState<Omit<RegistrationData, 'email'>>({
        organizationName: '',
        username: '',
        password: '',
    });
    const [error, setError] = useState<string | null>(null);
    const [traceId, setTraceId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!token) {
            setTokenError('Invalid registration link.');
            return;
        }
        decodeRegistrationToken(token)
            .then(res => setEmail(res.email))
            .catch(() => setTokenError('This registration link is invalid or has expired.'));
    }, [token]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await register({ ...formData, email });
            navigate('/login', { state: { registrationSuccess: true } });
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
            setTraceId(parseApiError(err)?.traceId ?? null);
        } finally {
            setLoading(false);
        }
    };

    if (tokenError) {
        return (
            <div className="auth-card">
                <h2>Invalid Link</h2>
                <p className="error-message">{tokenError}</p>
            </div>
        );
    }

    if (!email) {
        return (
            <div className="auth-card">
                <p className="subtitle">Verifying your link...</p>
            </div>
        );
    }

    return (
        <div className="auth-card">
            <h2>Create Account</h2>
            <p className="subtitle">Complete your registration</p>
            <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                    <label htmlFor="regEmail">Email Address</label>
                    <input
                        type="email"
                        id="regEmail"
                        name="email"
                        value={email}
                        readOnly
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="organizationName">Organization Name</label>
                    <input
                        type="text"
                        id="organizationName"
                        name="organizationName"
                        placeholder="Enter organization name"
                        value={formData.organizationName}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="username">Username</label>
                    <input
                        type="text"
                        id="username"
                        name="username"
                        placeholder="Enter username"
                        value={formData.username}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="regPassword">Password</label>
                    <input
                        type="password"
                        id="regPassword"
                        name="password"
                        placeholder="Create a password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                    />
                </div>
                <button type="submit" disabled={loading}>
                    {loading ? 'Creating account...' : 'Create Account'}
                </button>
                {error && (
                    <p className="error-message">
                        {error}
                        {traceId && <span className="error-trace">Reference: {traceId}</span>}
                    </p>
                )}
            </form>
        </div>
    );
};

export default RegistrationForm;